import type { ClockSnapshot } from "@sx/ui/clock";
/* What the catalog holds, in the few words a person uses for it.
 *
 * The door names twenty-odd kinds; a person needs five shelves: Episodes, Datasets,
 * Models, Robots and Environments, with Evaluations and Tables shown only when a
 * Project has any. Every kind sits on exactly one shelf and carries one plain type name,
 * and the record is exhaustive over the generated `CatalogEntryKind`, so a kind the door
 * adds fails to compile here until it has a place. */

import { stampDay, stampShort, stampTime, type IconName } from "@sx/ui";

import type { EntryKind } from "./api/types";
import { catalogName } from "./names";

export type Shelf =
  | "episodes"
  | "datasets"
  | "models"
  | "robots"
  | "worlds"
  | "evaluations"
  | "tables";

/** Shelves in the order the library shows them. */
export const SHELVES: readonly Shelf[] = [
  "episodes",
  "datasets",
  "models",
  "robots",
  "worlds",
  "evaluations",
  "tables",
];

/** Shelves shown only when the Project holds something on them. */
export const SHELVES_SHOWN_WHEN_HELD: ReadonlySet<Shelf> = new Set<Shelf>([
  "evaluations",
  "tables",
]);

export const SHELF_TITLE: Record<Shelf, string> = {
  episodes: "Episodes",
  datasets: "Datasets",
  models: "Models",
  robots: "Robots",
  worlds: "Worlds",
  evaluations: "Evaluations",
  tables: "Tables",
};

interface KindPlace {
  /** The shelf it is browsed on; a floor capture is browsed as its episodes. */
  readonly shelf: Shelf;
  /** What a person calls one, in a Type column or a page's eyebrow. */
  readonly type: string;
  /** What the trail names its kind among the shelf's others, where the shelf mixes kinds a
   *  person tells apart (an object is not a world); the shelf's title otherwise. */
  readonly group?: string;
}

export const KIND_PLACE: Record<EntryKind, KindPlace> = {
  capture: { shelf: "episodes", type: "Recording session" },
  dataset: { shelf: "datasets", type: "Dataset" },
  table: { shelf: "tables", type: "Table" },
  // What a training run trains on is datasets, so its choice of datasets and their
  // versions sit with them rather than on a shelf of their own.
  corpus: { shelf: "datasets", type: "Datasets a run trains on" },
  dataset_snapshot: { shelf: "datasets", type: "Dataset version" },
  training_checkpoint: { shelf: "models", type: "Checkpoint" },
  base_weights: { shelf: "models", type: "Base model" },
  deployable_policy: { shelf: "models", type: "Robot policy" },
  policy_artifact: { shelf: "models", type: "Policy program" },
  embodiment: { shelf: "robots", type: "Robot" },
  part: { shelf: "robots", type: "Part" },
  action_interface: { shelf: "robots", type: "Controls" },
  environment: { shelf: "worlds", type: "Environment", group: "Environments" },
  scene: { shelf: "worlds", type: "Scene", group: "Scenes" },
  object_asset: { shelf: "worlds", type: "Object", group: "Objects" },
  asset_pack: { shelf: "worlds", type: "Asset pack" },
  // The photos, videos, documents and scans a person imported about a place or object.
  source: { shelf: "worlds", type: "Source" },
  world_run: { shelf: "evaluations", type: "World run" },
  eval_suite: { shelf: "evaluations", type: "Test suite" },
  benchmark_evidence: { shelf: "evaluations", type: "Benchmark results" },
  robot_application: { shelf: "evaluations", type: "Application" },
  capability_approval: { shelf: "evaluations", type: "Approval" },
};

/** The drawing each kind is known by, wherever a record of it is named or linked. */
export const KIND_ICON: Record<EntryKind, IconName> = {
  capture: "episode",
  dataset: "dataset",
  table: "table",
  corpus: "layers",
  dataset_snapshot: "dataset",
  training_checkpoint: "checkpoint",
  base_weights: "model",
  deployable_policy: "model",
  policy_artifact: "code",
  embodiment: "robots",
  part: "package",
  action_interface: "sliders",
  environment: "world",
  scene: "scene",
  object_asset: "object",
  asset_pack: "package",
  source: "scan",
  world_run: "simulation",
  eval_suite: "experiment",
  benchmark_evidence: "experiment",
  robot_application: "deploy",
  capability_approval: "verified",
};

export function isShelf(value: unknown): value is Shelf {
  return typeof value === "string" && (SHELVES as readonly string[]).includes(value);
}

/** A record as a person names it: the name it carries inside, its own readable name, or
 *  its type and the day it was made — never a digest or a generated id. */
export function itemName(entry: {
  readonly kind: EntryKind;
  readonly name: string;
  readonly title?: string;
  readonly updated: string;
}, snapshot?: ClockSnapshot): string {
  if (entry.title) return entry.title;
  // A model's own name is its checkpoint id; models are told apart by when they were made.
  if (entry.kind === "training_checkpoint")
    return `${KIND_PLACE[entry.kind].type} ${stampDay(entry.updated, snapshot?.timeZone)} ${stampTime(entry.updated, snapshot?.timeZone)}`;
  return catalogName(entry.name) ?? `${KIND_PLACE[entry.kind].type} ${stampShort(entry.updated, snapshot)}`;
}
